import { format } from "date-fns";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Clock,
  Gauge,
  Info,
  Timer,
  TrendingUp,
} from "lucide-react";

import { FilterBar } from "../components/FilterBar";
import { PageHeader } from "../components/PageHeader";
import {
  BucketDecisionTimeChart,
  CapacityIndexChart,
  TicketMixChart,
  VolumeNormalizedChart,
  hasHeadcountData,
} from "../components/ProductivityCharts";
import { CapacityRunwayChart, CounterfactualChart } from "../components/ProjectionCharts";
import { ReadinessMatrix } from "../components/ReadinessMatrix";
import { Sparkline } from "../components/Sparkline";
import { StatCard } from "../components/StatCard";
import { StepShell, getStep } from "../components/StepShell";
import { TatTable } from "../components/TatTable";
import { Badge } from "../../../ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../ui/card";
import { Skeleton } from "../../../ui/skeleton";
import { useAutomationReadiness } from "../lib/api/automation-readiness";
import { useProductivitySummary } from "../lib/api/productivity";
import { useProductivityProjection } from "../lib/api/productivity-projection";
import type { SparkPoint } from "../lib/api/types";
import { useDashboardSearch, type DashboardSearchValue } from "../lib/useDashboardSearch";
import { cn } from "../lib/utils";

const SUBTITLE =
  "The before-and-after story — decision time, throughput, and capacity, pre- and post-Athena.";

/**
 * Pull-through and withdrawn rate are measured over a window shifted ~90 days back, so that every
 * cohort in them has had time to fund or be withdrawn. Measured raw they read 19% and 0.7% in the
 * most recent month against 63% and 24% a quarter earlier — right-censoring, not an improvement.
 *
 * The consequence is that these two cards cover a different period from every other card on the
 * page, which the user has no way to see. The sparkline's last point carries the real end date, so
 * it is used to say so out loud rather than adding a field to the response contract.
 */
function measuredThrough(metric: { sparkline: SparkPoint[] }): string | null {
  const last = metric.sparkline[metric.sparkline.length - 1];
  if (!last) return null;
  const parsed = new Date(last.date);
  return Number.isNaN(parsed.getTime()) ? null : format(parsed, "d MMM yyyy");
}

function maturityHint(base: string, metric: { sparkline: SparkPoint[] }): string {
  const through = measuredThrough(metric);
  return through
    ? `${base} Counts applications decided through ${through} only, so every cohort has had time to resolve.`
    : `${base} Counts only applications decided long enough ago to have resolved.`;
}

function TrendLabel({
  current,
  previous,
  invertGood = false,
}: {
  current: number;
  previous: number;
  invertGood?: boolean;
}) {
  const delta = current - previous;
  const down = delta < 0;
  const good = invertGood ? down : !down;
  const Icon = down ? ArrowDown : ArrowUp;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 text-xs font-medium tabular-nums",
        good ? "text-approve" : "text-amber",
      )}
    >
      <Icon className="size-3" />
      {Math.abs(delta).toFixed(1)} pts vs previous period
    </span>
  );
}

export function Productivity() {
  const search = useDashboardSearch();
  const { data, isPending, isError, error } = useProductivitySummary({
    range: search.range,
    office: search.office,
    compare: search.compare,
  });

  return (
    <StepShell step={getStep("productivity")}>
      <PageHeader title="Productivity" subtitle={SUBTITLE} />
      <FilterBar />

      {isError ? (
        <Card>
          <CardContent className="p-6 text-sm text-decline" role="alert">
            Could not load productivity data: {(error as Error).message}
          </CardContent>
        </Card>
      ) : isPending || !data ? (
        <div className="space-y-4">
          <Skeleton className="h-[360px] rounded-lg" />
          <Skeleton className="h-[320px] rounded-lg" />
        </div>
      ) : (
        <>
          {/* Section 1 — Single-Round vs Multi-Round Decisions */}
          <Card>
            <CardHeader>
              <CardTitle>Single-Round vs Multi-Round Decisions</CardTitle>
              <CardDescription>
                Turnaround hours by risk tier — expand a tier to see its ticket-size breakdown.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-4 lg:grid-cols-2">
                <TatTable title="Single-Round Decisions" view={data.singleRound} />
                <TatTable title="Multi-Round Decisions" view={data.multiRound} />
              </div>
              <p className="text-xs leading-relaxed text-muted-foreground">
                Multi-round decisions — where an application bounces through more than one review
                cycle before resolution — take substantially longer. This split shows exactly how
                much.
              </p>
            </CardContent>
          </Card>

          {/* Section 2 — Capacity */}
          <Card>
            <CardHeader>
              <CardTitle>Capacity: Volume &amp; Headcount Index</CardTitle>
              <CardDescription>Indexed to the first pre-go-live month (base = 100).</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <CapacityIndexChart series={data.capacitySeries} />
              {/* Both lines below describe the headcount series and the gap band between it and
                  volume. Neither is drawn while headcount is unavailable, so claiming a
                  "widening gap" would be describing something that is not on the chart. */}
              {hasHeadcountData(data.capacitySeries) ? (
                <>
                  <p className="text-xs text-muted-foreground">
                    Same team, more volume — the widening gap is the capacity Athena is creating.
                  </p>
                  <p className="inline-flex items-start gap-1.5 text-xs text-neutral-inactive">
                    <Info className="mt-0.5 size-3.5 shrink-0" />
                    <span>
                      Pre- and post-go-live points use different headcount attribution methods —{" "}
                      <a href="#methodology" className="underline decoration-dotted">
                        see methodology
                      </a>
                      .
                    </span>
                  </p>
                </>
              ) : (
                <p className="inline-flex items-start gap-1.5 text-xs text-neutral-inactive">
                  <Info className="mt-0.5 size-3.5 shrink-0" />
                  <span>
                    Showing volume only. Active headcount is not recorded for these teams yet, so
                    the headcount index and the capacity gap cannot be drawn.
                  </span>
                </p>
              )}
            </CardContent>
          </Card>

          {/* Section 3 — Volume-Normalized Decision Time */}
          <Card>
            <CardHeader>
              <CardTitle>Volume-Normalized Decision Time</CardTitle>
              <CardDescription>
                Applications decided per month against average first decision time.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <VolumeNormalizedChart series={data.capacitySeries} />
              <p className="text-xs text-muted-foreground">
                Decision time holds — or improves — even as volume grows.
              </p>
            </CardContent>
          </Card>

          {/* Section 4 — Ticket Size Mix */}
          <Card>
            <CardHeader>
              <CardTitle>Ticket Size Mix</CardTitle>
              <CardDescription>
                Share of decided applications by ticket-size bucket, and decision time inside each
                bucket.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <TicketMixChart series={data.ticketMixSeries} />
              <div className="space-y-3">
                <BucketDecisionTimeChart series={data.ticketMixSeries} />
                <p className="text-xs leading-relaxed text-muted-foreground">
                  If decision time improves inside each bucket, not just in the blended average,
                  speed gains aren&apos;t just a mix shift toward easier deals.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Section 5 — Pipeline Health */}
          <div className="grid gap-4 sm:grid-cols-2">
            <StatCard
              label="Pull-Through Rate"
              value={`${data.pullThroughRate.current.toFixed(1)}%`}
              icon={<Clock className="size-4" />}
              trend={
                <span className="ml-auto flex flex-col items-end gap-1">
                  <Sparkline data={data.pullThroughRate.sparkline} />
                  <TrendLabel
                    current={data.pullThroughRate.current}
                    previous={data.pullThroughRate.previousPeriod}
                  />
                </span>
              }
              hint={maturityHint("Approved applications that go on to fund.", data.pullThroughRate)}
            />
            <StatCard
              label="Withdrawn Rate"
              value={`${data.withdrawnRate.current.toFixed(1)}%`}
              icon={<Clock className="size-4" />}
              trend={
                <span className="ml-auto flex flex-col items-end gap-1">
                  <Sparkline data={data.withdrawnRate.sparkline} />
                  <TrendLabel
                    current={data.withdrawnRate.current}
                    previous={data.withdrawnRate.previousPeriod}
                    invertGood
                  />
                </span>
              }
              hint={maturityHint(
                "Applications pulled by the dealer or applicant before resolution.",
                data.withdrawnRate,
              )}
            />
          </div>

          {/* Section 6 — Athena Processing Time */}
          <div className="rounded-lg border border-neutral-inactive/40 bg-neutral-inactive-dim p-4">
            <div className="flex items-start justify-between gap-3">
              <p className="text-xs font-medium uppercase tracking-wider text-neutral-inactive">
                Athena Processing Time
              </p>
              <Timer className="size-4 text-neutral-inactive" />
            </div>
            <p className="mt-3 text-base font-medium text-neutral-inactive">Not yet measurable</p>
            <p className="mt-2 text-xs leading-relaxed text-neutral-inactive">
              {data.athenaProcessingTime.note}
            </p>
          </div>
        </>
      )}

      {/* Sections 7-9 load independently of the summary above. */}
      <ProjectionSections search={search} />
      <ReadinessSection search={search} />
    </StepShell>
  );
}

function ProjectionSections({ search }: { search: DashboardSearchValue }) {
  const { data, isPending, isError } = useProductivityProjection({
    range: search.range,
    office: search.office,
    compare: search.compare,
  });

  if (isError) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-decline" role="alert">
          Could not load projection data.
        </CardContent>
      </Card>
    );
  }

  if (isPending || !data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-[420px] rounded-lg" />
        <Skeleton className="h-[420px] rounded-lg" />
      </div>
    );
  }

  const { counterfactual, runway } = data;
  const noisy = counterfactual.fitQuality === "noisy_use_caution";

  return (
    <>
      {/* Section 7 — What Athena Already Changed */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-3">
            <CardTitle>What Athena Already Changed</CardTitle>
            <Badge
              variant="outline"
              className={cn(
                "gap-1 px-2 py-0.5 text-[11px] font-medium",
                noisy
                  ? "border-transparent bg-amber-dim text-amber"
                  : "border-border text-muted-foreground",
              )}
            >
              <AlertTriangle className="size-3" />
              {noisy ? "Noisy fit — use caution" : "Reliable fit"}
            </Badge>
          </div>
          <CardDescription>
            Average first decision time, actual vs. the pre-Athena trend carried forward.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <StatCard
            label="Attributable to Athena"
            value={`~${counterfactual.latestAttributableGapHours} hours`}
            icon={<Gauge className="size-4" />}
            hint="Faster per decision, net of the trend already underway before go-live."
          />
          <CounterfactualChart view={counterfactual} />
          {noisy ? (
            <p className="text-xs leading-relaxed text-amber">{counterfactual.fitNote}</p>
          ) : null}
          <p className="text-xs leading-relaxed text-muted-foreground">
            This looks backward. It isolates what already changed — it does not predict what&apos;s
            next.
          </p>
        </CardContent>
      </Card>

      {/* Section 8 — Capacity Runway */}
      <Card className="border-primary/25 bg-secondary/30">
        <CardHeader>
          <div className="flex flex-wrap items-center gap-3">
            <CardTitle>Capacity Runway</CardTitle>
            <Badge
              variant="outline"
              className="gap-1 border-primary/40 px-2 py-0.5 text-[11px] font-medium text-primary"
            >
              <TrendingUp className="size-3" />
              Projection
            </Badge>
          </div>
          <CardDescription>
            Three scenarios for decisions per analyst against the observed ceiling.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <CapacityRunwayChart view={runway} />
          <div className="grid gap-4 sm:grid-cols-3">
            {runway.scenarios.map((scenario) => {
              const months =
                runway.headroomMonthsEstimate.find((h) => h.scenario === scenario.name)
                  ?.monthsUntilCeiling ?? null;
              return (
                <StatCard
                  key={scenario.name}
                  label={scenario.label}
                  value={
                    months == null ? (
                      <span className="text-base font-medium text-neutral-inactive">
                        Does not reach ceiling in this projection window
                      </span>
                    ) : (
                      <span className="text-base font-medium">
                        Reaches ceiling in ~{months} months
                      </span>
                    )
                  }
                  muted={months == null}
                />
              );
            })}
          </div>
          {/* While headcount is unavailable the chart itself renders the note in its empty
              state, so repeating it here would print the same paragraph twice. */}
          {runway.scenarios.length > 0 ? (
            <p className="text-xs leading-relaxed text-muted-foreground">
              {runway.ceilingBasisNote}
            </p>
          ) : null}
          <p className="text-xs leading-relaxed text-muted-foreground">
            This looks forward. It&apos;s a planning tool with visible assumptions, not a guarantee.
          </p>
        </CardContent>
      </Card>
    </>
  );
}

function ReadinessSection({ search }: { search: DashboardSearchValue }) {
  const { data, isPending, isError } = useAutomationReadiness({
    range: search.range,
    office: search.office,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Automation Readiness Matrix</CardTitle>
        <CardDescription>
          Median decision time by ticket size and guarantor count — checkmarks mark cells clean
          enough to automate under the thresholds below.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isError ? (
          <p className="text-sm text-decline" role="alert">
            Could not load the readiness matrix.
          </p>
        ) : isPending || !data ? (
          <Skeleton className="h-[420px] rounded-lg" />
        ) : (
          <ReadinessMatrix matrix={data} />
        )}
        <p className="text-xs leading-relaxed text-muted-foreground">
          This tests whether the current small-ticket cutoff is even the right one — using evidence
          instead of an assumed threshold.
        </p>
      </CardContent>
    </Card>
  );
}
